// components/chat-surface/chat-canvas-cards.js
// ═══════════════════════════════════════════════════════════
// Canvas interaction cards for chat:
//   - Canvas updated (sage badge + thumbnail card)
//   - Canvas building (gold badge + spinning thumbnail)
//   - Canvas suggestion (gold card with Approve/Dismiss)
// ════════════════��══════════════════════════════════════════

// ── Canvas updated card (artifact complete) ──
export function CanvasUpdatedCard({ typeLabel }) {
  const thumbColors = {
    Page: ['#c8a96e','#7a9e82','#c8a96e','#7a9e82'],
    SVG: ['#9b8fc4','#c8a96e','#9b8fc4','#c8a96e'],
    Document: ['#7a9e82','#7a9e82','#c8a96e','#7a9e82'],
    Code: ['#9b8fc4','#9b8fc4','#c8a96e','#9b8fc4'],
    Data: ['#c8a96e','#9b8fc4','#c8a96e','#9b8fc4'],
  };
  const bars = thumbColors[typeLabel] || thumbColors.Page;

  return (
    <>
      <span style={{
        fontSize: 9, padding: '1px 6px', borderRadius: 4,
        border: '1px solid rgba(122,158,130,0.25)',
        background: 'rgba(122,158,130,0.08)', color: '#7a9e82',
        display: 'inline-block', marginTop: 3, fontFamily: 'Georgia, serif',
      }}>
        → canvas updated
      </span>
      <div style={{
        margin: '2px 0 2px 9px', padding: '5px 8px',
        background: '#111', border: '1px solid rgba(200,169,110,0.25)',
        borderRadius: 6, display: 'flex', alignItems: 'center',
        gap: 7, cursor: 'pointer',
      }}>
        <div style={{
          width: 34, height: 24, background: '#07070a',
          border: '1px solid #1e1e2a', borderRadius: 3, flexShrink: 0,
          display: 'flex', alignItems: 'flex-end', gap: 1.5, padding: 3,
        }}>
          {[55, 90, 40, 70].map((h, j) => (
            <div key={j} style={{ flex: 1, height: `${h}%`, background: `${bars[j]}77`, borderRadius: 1 }} />
          ))}
        </div>
        <div>
          <div style={{ fontSize: 9, color: '#c8a96e', fontFamily: 'Georgia, serif' }}>Canvas updated</div>
          <div style={{ fontSize: 9, color: '#333', fontFamily: 'Georgia, serif', marginTop: 1 }}>{typeLabel} — click to view</div>
        </div>
      </div>
    </>
  );
}

// ── Canvas building card (streaming) ──
export function CanvasBuildingCard({ typeLabel }) {
  return (
    <>
      <span style={{
        fontSize: 9, padding: '1px 6px', borderRadius: 4,
        border: '1px solid rgba(200,169,110,0.25)',
        background: 'rgba(200,169,110,0.08)', color: '#c8a96e',
        display: 'inline-block', marginTop: 3, fontFamily: 'Georgia, serif',
      }}>
        canvas command
      </span>
      <div style={{
        margin: '2px 0 2px 9px', padding: '5px 8px',
        background: '#111', border: '1px solid rgba(200,169,110,0.15)',
        borderRadius: 6, display: 'flex', alignItems: 'center',
        gap: 7, animation: 'ws-artifact-building 1.5s ease-in-out infinite',
      }}>
        <div style={{
          width: 34, height: 24, background: '#07070a',
          border: '1px solid #1e1e2a', borderRadius: 3, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{
            fontSize: 10, color: '#c8a96e',
            animation: 'ws-artifact-spin 2s linear infinite',
            display: 'inline-block',
          }}>◎</span>
        </div>
        <div>
          <div style={{ fontSize: 9, color: '#c8a96e', fontFamily: 'Georgia, serif' }}>Building {typeLabel}...</div>
          <div style={{ fontSize: 9, color: '#444', fontFamily: 'Georgia, serif', fontStyle: 'italic', marginTop: 1 }}>Rendering on canvas when ready</div>
        </div>
      </div>
    </>
  );
}

// ── Canvas suggestion card (Approve/Dismiss) ──
export function CanvasSuggestionCard({ suggestion, aiName, onApprove, onDismiss }) {
  if (!suggestion) return null;
  return (
    <div style={{ marginBottom: 18, maxWidth: 400 }}>
      <div style={{
        background: 'rgba(200,169,110,0.03)',
        border: '1px solid rgba(200,169,110,0.15)',
        borderRadius: 6, padding: '6px 8px',
      }}>
        <div style={{ fontSize: 9, color: '#c8a96e', fontFamily: 'Georgia, serif', marginBottom: 3 }}>
          Canvas suggestion from {aiName || 'Shift'}
        </div>
        <div style={{ fontSize: 10, color: '#666', fontFamily: 'Georgia, serif', lineHeight: 1.45, marginBottom: 5 }}>
          &ldquo;{suggestion}&rdquo;
        </div>
        <div style={{ display: 'flex', gap: 5 }}>
          <button onClick={onApprove} style={{
            fontSize: 9, color: '#7a9e82', background: 'rgba(122,158,130,0.08)',
            border: '1px solid rgba(122,158,130,0.25)', borderRadius: 4,
            padding: '2px 8px', cursor: 'pointer', fontFamily: 'Georgia, serif',
          }}>
            Approve
          </button>
          <button onClick={onDismiss} style={{
            fontSize: 9, color: '#333', background: '#111',
            border: '1px solid #1e1e2a', borderRadius: 4,
            padding: '2px 8px', cursor: 'pointer', fontFamily: 'Georgia, serif',
          }}>
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Choice chips ──
export function ChatChips({ chips, onSelect, loading }) {
  if (!chips || chips.length === 0 || loading) return null;
  return (
    <div style={{ marginBottom: 18, maxWidth: 400, width: '100%', alignSelf: 'center' }}>
      <div style={{
        fontSize: 9, color: '#555', fontFamily: 'Georgia, serif',
        letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8,
      }}>
        Choose one
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {chips.map((chip, i) => (
          <button
            key={i}
            onClick={() => onSelect(chip)}
            style={{
              background: '#111', border: '1px solid #2a2a2a',
              borderRadius: 8, padding: '8px 14px',
              cursor: 'pointer', display: 'flex', alignItems: 'center',
              gap: 10, textAlign: 'left', transition: 'border-color 0.15s, background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(200,169,110,0.3)'; e.currentTarget.style.background = '#141414'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.background = '#111'; }}
          >
            <span style={{
              width: 20, height: 20, borderRadius: '50%',
              background: 'rgba(200,169,110,0.08)',
              border: '1px solid rgba(200,169,110,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 9, color: '#c8a96e', fontFamily: 'Georgia, serif',
              fontWeight: 700, flexShrink: 0,
            }}>
              {i + 1}
            </span>
            <span style={{ fontSize: 11, color: '#d4d4d4', fontFamily: 'Georgia, serif', lineHeight: 1.4 }}>
              {chip}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Keyframes for building animation ──
export function CanvasCardStyles() {
  return (
    <style jsx global>{`
      @keyframes ws-artifact-building {
        0%, 100% { opacity: 0.7; }
        50% { opacity: 1; }
      }
      @keyframes ws-artifact-spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `}</style>
  );
}
